// period.ts — the period WORDS on the screen (ADR-082 §4 · §5): one label set in
// `common:period`, N through `formatNumber` with the plural keys; the exact start
// or the range that stands under a word (the picker's second line, the chip's
// hover), from the shared calendar math in the SCREEN's zone, on the project's
// week start, at the read's clock. The browser decides nothing here — the server
// picks the rows; these are captions and pre-fills (ADR-082 §6).

import {
  addDays,
  dayIn,
  parsePeriod,
  resolvePeriodDays,
  type CalendarDay,
  type DayPair,
  type Period,
  type PeriodWord,
  type WeekStart,
} from '@ems-app/schemas/common/period';
import { formatDate, formatDayRange, formatNumber } from './format.js';

/** The screen's clock for its period words: ITS zone (`useDisplayZone`), the project's week start, the read's arrival. */
export interface PeriodContext {
  zone: string;
  weekStart: WeekStart;
  /** Epoch ms — a read's own clock (`dataUpdatedAt`), so every caption agrees with the rows. */
  now: number;
}

/** A translator — `useTranslation`'s `t`; the keys are namespaced (`common:…`), so any namespace's works. */
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

const WORD_KEY: Partial<Record<PeriodWord, string>> = {
  today: 'today',
  yesterday: 'yesterday',
  this_week: 'thisWeek',
  this_month: 'thisMonth',
  this_year: 'thisYear',
  previous_week: 'previousWeek',
  previous_month: 'previousMonth',
  previous_year: 'previousYear',
  all: 'all',
};
const DAYS_OF: Partial<Record<PeriodWord, number>> = { last_7d: 7, last_30d: 30, last_90d: 90 };
const HOURS_OF: Partial<Record<PeriodWord, number>> = { last_24h: 24, last_6h: 6, last_1h: 1 };
const HOUR_MS = 3_600_000;

/** Today as the SCREEN zone's calendar day — the ceiling of every "any day up to today" picker and the default of a
 *  lifecycle day (ADR-082 §6: the screen's zone, never the browser's clock, which says yesterday in Riyadh between its
 *  midnight and UTC's). Read it where the zone is known (`useDisplayZone`). */
export function todayIn(zone: string): CalendarDay {
  return dayIn(zone, Date.now());
}

/** A word's label — "Last 7 days" with its number through the locale's digits and the plural form. */
export function periodWordLabel(word: PeriodWord, t: Translate): string {
  const days = DAYS_OF[word];
  if (days) return t('common:period.lastDays', { count: days, n: formatNumber(days) });
  const hours = HOURS_OF[word];
  if (hours) return t('common:period.lastHours', { count: hours, n: formatNumber(hours) });
  return t(`common:period.${WORD_KEY[word] ?? word}`);
}

/** The days a period covers in the screen's zone — `first` and `last` both included — or null (the hour words, `all`). */
export function periodDays(period: Period, ctx: PeriodContext): DayPair | null {
  const r = resolvePeriodDays(period, { today: dayIn(ctx.zone, ctx.now), weekStart: ctx.weekStart });
  return r.kind === 'days' ? { first: r.first, last: addDays(r.next, -1) } : null;
}

function inThisYear(p: DayPair, today: CalendarDay): boolean {
  const y = today.slice(0, 4);
  return p.first.slice(0, 4) === y && p.last.slice(0, 4) === y;
}

/** What stands UNDER a word (the picker's second line) and on the chip's hover: the day itself ("Fri 10 Oct"), "since
 *  Sat 4 Oct" for a rolling word, the whole range for a calendar period ("5–11 Oct"); a pair as its full dates; nothing
 *  for `all`. The hour words say their instant in the screen's zone. */
export function periodCaption(period: Period, ctx: PeriodContext, t: Translate): string | null {
  if (typeof period === 'object') return formatDayRange(period.first, period.last, { year: true });
  const hours = HOURS_OF[period];
  if (hours) return t('common:period.since', { day: formatDate(ctx.now - hours * HOUR_MS, 'weekdayDayMonthTime', { timeZone: ctx.zone }) ?? '' });
  const days = periodDays(period, ctx);
  if (!days) return null;
  if (period === 'today' || period === 'yesterday') return formatDate(days.first, 'weekdayDayMonth');
  if (DAYS_OF[period]) return t('common:period.since', { day: formatDate(days.first, 'weekdayDayMonth') ?? '' });
  return formatDayRange(days.first, days.last, { year: !inThisYear(days, dayIn(ctx.zone, ctx.now)) });
}

/** The chip's text for a value as the URL carries it: a word's label; a pair as "1–7 Oct", the year only when it is not
 *  this year ("7 Oct" for one day); a value the grammar cannot read, as is (tolerant — the read drops it). */
export function periodValueLabel(value: string, ctx: PeriodContext, t: Translate): string {
  const p = parsePeriod(value);
  if (p === null) return value;
  if (typeof p === 'string') return periodWordLabel(p, t);
  return formatDayRange(p.first, p.last, { year: !inThisYear(p, dayIn(ctx.zone, ctx.now)) });
}

/** The chip's hover: a word with its exact start ("Last 7 days · since Sat 4 Oct"); a pair with its full dates. */
export function periodValueHover(value: string, ctx: PeriodContext, t: Translate): string {
  const p = parsePeriod(value);
  if (p === null) return value;
  const caption = periodCaption(p, ctx, t);
  if (typeof p === 'object') return caption ?? value;
  const label = periodWordLabel(p, t);
  return caption ? `${label} · ${caption}` : label;
}
